import { useAuth, useUser } from '@clerk/react-router';
import {
  BuildingOffice2Icon,
  CheckBadgeIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Button } from '~/components/buttons/Button/Button';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import {
  getColleagueInvitationEligibility,
  verifyColleagueInvitation,
} from '~/features/employer/colleague-invites/api';
import { INVITE_CONFLICT_MESSAGES } from '~/features/employer/colleague-invites/inviteConflictMessages';
import {
  EmployerUserInvitationConflict,
  VerifyEmployerUserInvitationResponse,
} from '~/features/employer/colleague-invites/types';
import { paths } from '~/routing/routes';

const INVITE_CONFLICT_CY: Record<EmployerUserInvitationConflict, string> = {
  provider_account: 'invite-conflict-provider',
  other_org: 'invite-conflict-org',
  already_member: 'invite-conflict-member',
};

const buildInvitePath = (path: string, code: string) => `${path}?code=${encodeURIComponent(code)}`;

const normalize = (email: string) => email.trim().toLowerCase();

/**
 * [MCV-1579] Whether the signed-in Clerk session can actually accept this
 * invitation.
 *
 * The API accepts only when the invited address is among the session's
 * VERIFIED Clerk emails (`invitation_service.accept_invitation`). That check is
 * correct — it is what stops any signed-in user consuming someone else's
 * invite — but the page used to ignore it entirely, offer "Accept Invitation"
 * to whoever was signed in, and surface the resulting 400 as a dead end
 * ("Authenticated user email does not match the invitation email" + a Go to
 * Homepage button). QA hit exactly that. Deciding it up front is what lets the
 * page offer a way out instead of a wall.
 */
type SessionMatch = 'signed-out' | 'ready' | 'unverified' | 'wrong-account';

const sessionMatch = (
  data: VerifyEmployerUserInvitationResponse | null,
  isSignedIn: boolean | undefined,
  clerkEmails: { emailAddress: string; verification?: { status?: string | null } | null }[]
): SessionMatch | null => {
  if (!data) return null;
  if (!isSignedIn) return 'signed-out';
  const invitationEmail = normalize(data.invitationEmail);
  const matching = clerkEmails.filter((email) => normalize(email.emailAddress) === invitationEmail);
  if (matching.some((email) => email.verification?.status === 'verified')) return 'ready';
  return matching.length > 0 ? 'unverified' : 'wrong-account';
};

export const EmployerUserInvitationVerify = () => {
  const { isSignedIn, signOut } = useAuth();
  const { user } = useUser();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code') ?? '';
  const [data, setData] = useState<VerifyEmployerUserInvitationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  // [MCV-1796] Whether accepting would 409 for this account, learned before the
  // button is offered. `undefined` while the check runs; a failed check leaves
  // `null` so Accept still works and the accept page reports the real error.
  const [conflict, setConflict] = useState<EmployerUserInvitationConflict | null | undefined>(
    undefined
  );
  // The mocked Clerk used by component tests has no `emailAddresses` array, so
  // this stays defensive rather than assuming the resource shape.
  const clerkEmails = user?.emailAddresses ?? [];
  const match = sessionMatch(data, isSignedIn, clerkEmails);

  useEffect(() => {
    setConflict(undefined);
    if (match !== 'ready') return;
    let active = true;
    getColleagueInvitationEligibility(code)
      .then((response) => {
        if (active) setConflict(response.conflict);
      })
      .catch(() => {
        if (active) setConflict(null);
      });
    return () => {
      active = false;
    };
  }, [match, code]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setInvalid(false);

    if (!code) {
      setLoading(false);
      setInvalid(true);
      return;
    }

    verifyColleagueInvitation(code)
      .then((response) => {
        if (!active) return;
        setData(response);
        setInvalid(response.status !== 'PENDING');
      })
      .catch(() => {
        if (!active) return;
        setInvalid(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [code]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-base">
        <LoadingSpinner size={20} />
      </div>
    );
  }

  if (invalid || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-base p-10">
        <div className="w-full max-w-lg rounded-lg bg-white p-12 text-center shadow-xl">
          {/* `bg-error-50` is NOT a light tint — the `-50` step is the saturated
              mid-tone, so the icon was painting red on red and rendered as a
              blank disc. `danger-subtle` is the tint built to pair with
              `text-danger`. [MCV-1579] */}
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-danger-subtle">
            <ExclamationTriangleIcon className="h-7 w-7 text-danger" />
          </div>
          <h1 className="mb-3 text-2xl font-semibold text-content-primary">Invitation Not Found</h1>
          <p className="mb-7 text-sm leading-relaxed text-content-secondary">
            This invitation link is invalid, expired, or already used.
          </p>
          <Link
            to={paths.landing}
            className="inline-flex items-center justify-center rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-white hover:bg-primary-600"
          >
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  const acceptPath = buildInvitePath(paths.employerUserInvitation.accept, code);
  const verifyPath = buildInvitePath(paths.employerUserInvitation.verify, code);
  const signUpPath = `${paths.publicAuth.signUpEmployer}?invitationCode=${encodeURIComponent(code)}`;
  const signedInAs = user?.primaryEmailAddress?.emailAddress;
  const hasConflict = match === 'ready' && conflict != null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-base p-10">
      <div className="w-full max-w-lg rounded-lg bg-white p-12 shadow-xl">
        <div className="mb-7 inline-flex items-center gap-3 rounded-lg border border-grey-900 bg-grey-950 px-5 py-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] bg-primary-50">
            <BuildingOffice2Icon
              data-cy="invite-organization-icon"
              aria-hidden="true"
              className="h-6 w-6 text-white"
            />
          </div>
          <div>
            <div className="font-semibold text-content-primary">{data.employerName}</div>
            <div className="text-xs text-content-secondary">Employer Organization</div>
          </div>
        </div>

        <h1 className="mb-3 text-2xl font-semibold text-content-primary">
          You have been invited to join {data.employerName}
        </h1>
        {match !== 'wrong-account' && !hasConflict && (
          <p className="mb-8 text-sm leading-relaxed text-content-secondary">
            {match === 'signed-out' ? (
              <>
                This invitation is for <strong>{data.invitationEmail}</strong>. Create an account
                with this email to join {data.employerName}.
              </>
            ) : (
              <>Accepting adds you to {data.employerName}.</>
            )}
          </p>
        )}

        {match === 'wrong-account' && (
          <div
            data-cy="invite-wrong-account"
            className="mb-8 rounded-lg border border-grey-900 bg-grey-950 p-5"
          >
            <p className="text-sm leading-relaxed text-content-secondary">
              This invitation was sent to <strong>{data.invitationEmail}</strong>, but you&apos;re
              signed in
              {signedInAs ? (
                <>
                  {' '}
                  as <strong>{signedInAs}</strong>
                </>
              ) : (
                ' with a different account'
              )}
              . Sign out, then create an account with <strong>{data.invitationEmail}</strong> to
              accept it.
            </p>
          </div>
        )}

        {hasConflict && (
          <div
            data-cy={INVITE_CONFLICT_CY[conflict]}
            className="mb-8 rounded-lg border border-grey-900 bg-grey-950 p-5"
          >
            <p className="text-sm leading-relaxed text-content-secondary">
              {INVITE_CONFLICT_MESSAGES[conflict]}
            </p>
          </div>
        )}

        {match === 'unverified' && (
          <div
            data-cy="invite-unverified-email"
            className="mb-8 rounded-lg border border-grey-900 bg-grey-950 p-5"
          >
            <p className="text-sm leading-relaxed text-content-secondary">
              <strong>{data.invitationEmail}</strong> is on your account but has not been verified
              yet. Verify it from your account settings, then reopen this link to accept.
            </p>
          </div>
        )}

        {match !== 'wrong-account' && match !== 'unverified' && !hasConflict && (
          <div className="mb-8 space-y-2.5 rounded-lg border border-grey-900 bg-grey-950 p-5">
            <div className="flex items-center gap-2.5 text-left">
              <CheckBadgeIcon className="h-[18px] w-[18px] shrink-0 text-primary-50" />
              <span className="text-sm text-content-secondary">
                No new employer organization or onboarding settings will be created
              </span>
            </div>
          </div>
        )}

        {match === 'wrong-account' ? (
          <Button
            type="button"
            colorVariant="primary"
            cyTag="invite-sign-out"
            // Clerk brings the browser back to this same invite link once the
            // session is cleared, so the code is never dropped on the way out.
            onClick={() => {
              signOut({ redirectUrl: verifyPath }).catch(() => {
                window.location.assign(verifyPath);
              });
            }}
          >
            Sign out and continue
          </Button>
        ) : match === 'signed-out' ? (
          <div className="flex justify-center">
            <Button
              type="button"
              colorVariant="primary"
              cyTag="invite-sign-up"
              onClick={() => window.location.assign(signUpPath)}
            >
              Create Account to Accept
            </Button>
          </div>
        ) : hasConflict ? (
          // Signing out cannot fix any of the conflicts, so no sign-out is offered.
          <Link
            to={paths.landing}
            className="inline-flex items-center justify-center rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-white hover:bg-primary-600"
          >
            Go to Homepage
          </Link>
        ) : (
          <Button
            type="button"
            colorVariant="primary"
            disabled={match === 'unverified' || (match === 'ready' && conflict === undefined)}
            onClick={() => window.location.assign(acceptPath)}
          >
            Accept Invitation
          </Button>
        )}
      </div>
    </div>
  );
};
